import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  Descriptions,
  Alert,
} from "antd";
import { useGlobal } from "../contexts/GlobalContext";
import { useCurrentProject } from "../hooks/useCurrentProject";
import request from "../utils/request";
import { getErrorText } from "../utils/errorText";

const { Text, Paragraph } = Typography;

// 事件标注(站点动作)管理:对 SDK 在页面上选取/标注生成的「站点动作」做查看/编辑/删除/发布。
// 数据模型见后端 site_action_maps:动作元信息 + 草稿元素(selector/动作类型)。

interface ActionRow {
  id: number;
  action_key: string;
  name: string;
  description?: string;
  status: string;
  agent_id?: number;
  draft_config?: Record<string, any>;
  updated_at?: string;
}

interface ElementRow {
  id: number;
  label?: string;
  selector: string;
  action_type: string;
  risk?: string;
  requires_confirm?: boolean;
}

const ACTION_TYPES = ["read", "extract", "fill", "click", "navigate", "submit"];
const REGION_TYPES = [
  { label: "可操作区", value: "operable" },
  { label: "可阅读区", value: "readable" },
];
const CONFIRM_POLICIES = [
  { label: "无需确认", value: "none" },
  { label: "中等", value: "medium" },
  { label: "高", value: "high" },
  { label: "每次确认", value: "every_time" },
];

// 一个项目可绑定多个站点:refer 为逗号分隔的网址列表(后端 utils.SplitRefer 同款规则)。
// 这里拆成可选站点列表;额外容忍空白分隔,缺协议时补 https。
const parseBoundSites = (refer?: string): string[] => {
  return (refer || "")
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (/^https?:\/\//i.test(s) ? s : `https://${s}`));
};

// 把标注临时密钥注入到「被绑定网站」URL 上,SDK(hongmo-core.js)读 ?__hongmo_annotate=<token>
// 自动进入标注选取模式。传入的应是单个已规范化的站点 URL。
const buildAnnotateUrl = (siteUrl: string, token: string): string => {
  if (!siteUrl) return "";
  try {
    const url = new URL(siteUrl);
    url.searchParams.set("__hongmo_annotate", token);
    return url.toString();
  } catch {
    const sep = siteUrl.includes("?") ? "&" : "?";
    return `${siteUrl}${sep}__hongmo_annotate=${encodeURIComponent(token)}`;
  }
};

const EventAnnotations: React.FC = () => {
  const { API_BASE_URL } = useGlobal();
  const { project } = useCurrentProject();
  const projectId = project?.id;
  const [list, setList] = useState<ActionRow[]>([]);
  const [loading, setLoading] = useState(false);

  // 编辑 / 查看 modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ActionRow | null>(null);
  const [form] = Form.useForm();
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState<{ action: ActionRow; elements: ElementRow[] } | null>(null);
  const [tokenHint, setTokenHint] = useState<string>("");
  const [annotateUrl, setAnnotateUrl] = useState<string>("");
  const [siteModalOpen, setSiteModalOpen] = useState(false);

  const loadList = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/site-actions`, { params: { projectId } });
      setList(res.data?.data || []);
    } catch (e) {
      message.error(getErrorText(e, "加载事件标注失败"));
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, projectId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ actionType: "fill", regionType: "operable", confirmPolicy: "medium", pageRange: "/**", risk: "medium" });
    setEditOpen(true);
  };

  const openEdit = async (row: ActionRow) => {
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/site-actions/${row.id}`);
      const detail = res.data?.data;
      const el: ElementRow | undefined = (detail?.elements || [])[0];
      const cfg = detail?.action?.draft_config || {};
      setEditing(row);
      form.setFieldsValue({
        name: detail?.action?.name,
        actionKey: detail?.action?.action_key,
        description: detail?.action?.description,
        regionType: cfg.regionType || "operable",
        confirmPolicy: cfg.confirmPolicy || "medium",
        pageRange: cfg.pageRange || "/**",
        annotation: cfg.annotation || "",
        selector: el?.selector || "",
        actionType: el?.action_type || "fill",
        label: el?.label || "",
        risk: el?.risk || "medium",
      });
      setEditOpen(true);
    } catch (e) {
      message.error(getErrorText(e, "加载详情失败"));
    }
  };

  const submitEdit = async () => {
    const v = await form.validateFields();
    try {
      const payload = {
        projectId,
        actionKey: v.actionKey || undefined,
        name: v.name,
        description: v.description,
        draftConfig: {
          regionType: v.regionType,
          confirmPolicy: v.confirmPolicy,
          pageRange: v.pageRange,
          annotation: v.annotation,
        },
        elements: v.selector
          ? [{ label: v.label || v.name, selector: v.selector, actionType: v.actionType, risk: v.risk }]
          : [],
      };
      const res = await request.post(`${API_BASE_URL}/api/v3/site-actions`, payload);
      if (res.data?.code === 200) {
        message.success(editing ? "已保存" : "已创建");
        setEditOpen(false);
        loadList();
      } else {
        message.error(res.data?.msg || "保存失败");
      }
    } catch (e) {
      message.error(getErrorText(e, "保存失败"));
    }
  };

  const openView = async (row: ActionRow) => {
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/site-actions/${row.id}`);
      setViewData(res.data?.data);
      setViewOpen(true);
    } catch (e) {
      message.error(getErrorText(e, "加载详情失败"));
    }
  };

  const doPublish = async (row: ActionRow) => {
    try {
      const res = await request.post(`${API_BASE_URL}/api/v3/site-actions/${row.id}/publish`);
      if (res.data?.code === 200) {
        message.success("已发布,SDK 可在页面查看");
        loadList();
      } else {
        message.error(res.data?.msg || "发布失败");
      }
    } catch (e) {
      message.error(getErrorText(e, "发布失败"));
    }
  };

  const doDelete = async (row: ActionRow) => {
    try {
      const res = await request.delete(`${API_BASE_URL}/api/v3/site-actions/${row.id}`);
      if (res.data?.code === 200) {
        message.success("已删除");
        loadList();
      } else {
        message.error(res.data?.msg || "删除失败");
      }
    } catch (e) {
      message.error(getErrorText(e, "删除失败"));
    }
  };

  const doDisable = async (row: ActionRow) => {
    try {
      const res = await request.post(`${API_BASE_URL}/api/v3/site-actions/${row.id}/disable`);
      if (res.data?.code === 200) { message.success("已停用，页面操作立即阻断"); loadList(); }
      else message.error(res.data?.msg || "停用失败");
    } catch (e) { message.error(getErrorText(e, "停用失败")); }
  };

  // 入口:0 个站点 → 提示;1 个 → 直接进入;多个 → 弹出站点选择(选择动作本身即用户手势,
  // 仍可同步 window.open 规避弹窗拦截)。
  const enterAnnotateMode = () => {
    if (!projectId) return;
    const sites = parseBoundSites(project?.refer);
    if (sites.length === 0) {
      message.warning("当前项目尚未绑定网站,请先到「网站接入」绑定网址后再进入标注模式");
      return;
    }
    if (sites.length === 1) {
      openAnnotate(sites[0]);
      return;
    }
    setSiteModalOpen(true);
  };

  // 对指定站点开标注:先同步打开空白页(规避「await 之后 window.open」被拦截),取密钥后跳转。
  const openAnnotate = async (site: string) => {
    if (!projectId || !site) return;
    setSiteModalOpen(false);
    const win = window.open("", "_blank");
    try {
      const res = await request.post(`${API_BASE_URL}/api/v3/site-actions/annotation-token`, { projectId });
      const token = res.data?.data?.token || res.data?.data?.annotationToken || res.data?.data;
      if (!token) throw new Error("empty token");
      const target = buildAnnotateUrl(site, String(token));
      setAnnotateUrl(target);
      setTokenHint(
        `已在新标签页打开「${target}」并注入标注密钥;SDK 会自动进入标注选取模式。` +
          (project?.key_value ? `(项目 key:${project.key_value})` : "")
      );
      if (win) {
        win.location.href = target;
      } else {
        // 弹窗被拦截:退回为可点击链接,用户手动跳转。
        message.warning("浏览器拦截了弹窗,请点击下方链接进入标注页面");
      }
    } catch (e) {
      if (win) win.close();
      message.error(getErrorText(e, "生成标注密钥失败"));
    }
  };

  const columns = [
    { title: "名称", dataIndex: "name", key: "name", render: (v: string) => <Text strong>{v}</Text> },
    { title: "动作 key", dataIndex: "action_key", key: "action_key", render: (v: string) => <Text code>{v}</Text> },
    {
      title: "区域类型",
      key: "regionType",
      render: (_: any, r: ActionRow) => r.draft_config?.regionType === "readable" ? <Tag color="blue">可阅读</Tag> : <Tag color="green">可操作</Tag>,
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      render: (s: string) => (s === "published" ? <Tag color="success">已发布</Tag> : <Tag>草稿</Tag>),
    },
    { title: "更新时间", dataIndex: "updated_at", key: "updated_at", render: (v: string) => (v ? new Date(v).toLocaleString() : "-") },
    {
      title: "操作",
      key: "ops",
      width: 280,
      render: (_: any, r: ActionRow) => (
        <Space size="small" wrap>
          <Button size="small" onClick={() => openView(r)}>查看</Button>
          <Button size="small" type="link" onClick={() => openEdit(r)}>编辑</Button>
          <Button size="small" type="link" onClick={() => doPublish(r)}>发布</Button>
          {r.status === "published" && <Popconfirm title="确认立即停用该动作？" onConfirm={() => doDisable(r)}><Button size="small" danger type="link">停用</Button></Popconfirm>}
          <Popconfirm title="确认删除该事件标注?" onConfirm={() => doDelete(r)} okText="删除" cancelText="取消">
            <Button size="small" type="link" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="事件标注管理"
      extra={
        <Space>
          <span>项目:</span>
          <Tag color="blue">{project?.name || "—"}</Tag>
          <Button onClick={enterAnnotateMode}>进入标注模式</Button>
          <Button type="primary" onClick={openCreate}>新建标注</Button>
        </Space>
      }
    >
      <Alert
        type="info"
        message="事件标注 = Agent 可在访客页面上操作/阅读的区域。点「进入标注模式」会直接打开本项目绑定的网站并注入标注密钥,在真实页面上圈选标注;发布后 SDK 可在页面上回看高亮(Hongmo.annotate.view())。"
        style={{ marginBottom: 16 }}
      />
      {tokenHint && (
        <Alert
          type="success"
          closable
          onClose={() => {
            setTokenHint("");
            setAnnotateUrl("");
          }}
          message={tokenHint}
          description={
            annotateUrl ? (
              <a href={annotateUrl} target="_blank" rel="noopener noreferrer">
                未自动打开?点此进入标注页面 →
              </a>
            ) : undefined
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <Table rowKey="id" loading={loading} columns={columns as any} dataSource={list} pagination={{ pageSize: 10 }} />

      {/* 新建 / 编辑 */}
      <Modal
        title={editing ? "编辑事件标注" : "新建事件标注"}
        open={editOpen}
        onOk={submitEdit}
        onCancel={() => setEditOpen(false)}
        okText="保存"
        cancelText="取消"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: "请输入名称" }]}>
            <Input placeholder="如:站内搜索框" />
          </Form.Item>
          <Form.Item name="actionKey" label="动作 key(留空则由名称生成,编辑时不可改)">
            <Input placeholder="search_box" disabled={!!editing} />
          </Form.Item>
          <Space style={{ display: "flex" }} align="start">
            <Form.Item name="regionType" label="区域类型" style={{ flex: 1 }}>
              <Select options={REGION_TYPES} />
            </Form.Item>
            <Form.Item name="actionType" label="动作类型" style={{ flex: 1 }}>
              <Select options={ACTION_TYPES.map((t) => ({ label: t, value: t }))} />
            </Form.Item>
            <Form.Item name="confirmPolicy" label="确认策略" style={{ flex: 1 }}>
              <Select options={CONFIRM_POLICIES} />
            </Form.Item>
          </Space>
          <Form.Item name="selector" label="目标选择器(selector)">
            <Input placeholder="#search-input / .btn-submit" />
          </Form.Item>
          <Form.Item name="label" label="元素标签">
            <Input placeholder="搜索框" />
          </Form.Item>
          <Form.Item name="pageRange" label="页面范围(通配符)">
            <Input placeholder="/** 或 /p/*" />
          </Form.Item>
          <Form.Item name="annotation" label="给 AI 的注释">
            <Input.TextArea rows={2} placeholder="这是什么、何时用" />
          </Form.Item>
          <Form.Item name="risk" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看 */}
      <Modal title="查看事件标注" open={viewOpen} onCancel={() => setViewOpen(false)} footer={null} width={620}>
        {viewData && (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="名称">{viewData.action.name}</Descriptions.Item>
              <Descriptions.Item label="动作 key">{viewData.action.action_key}</Descriptions.Item>
              <Descriptions.Item label="状态">{viewData.action.status}</Descriptions.Item>
              <Descriptions.Item label="区域类型">{viewData.action.draft_config?.regionType || "-"}</Descriptions.Item>
              <Descriptions.Item label="确认策略">{viewData.action.draft_config?.confirmPolicy || "-"}</Descriptions.Item>
              <Descriptions.Item label="页面范围">{viewData.action.draft_config?.pageRange || "-"}</Descriptions.Item>
              <Descriptions.Item label="注释">{viewData.action.draft_config?.annotation || "-"}</Descriptions.Item>
              <Descriptions.Item label="描述">{viewData.action.description || "-"}</Descriptions.Item>
            </Descriptions>
            <Paragraph strong style={{ marginTop: 16 }}>元素({viewData.elements?.length || 0})</Paragraph>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={viewData.elements || []}
              columns={[
                { title: "标签", dataIndex: "label", key: "label" },
                { title: "selector", dataIndex: "selector", key: "selector", render: (v: string) => <Text code>{v}</Text> },
                { title: "动作", dataIndex: "action_type", key: "action_type" },
                { title: "风险", dataIndex: "risk", key: "risk" },
              ]}
            />
          </>
        )}
      </Modal>

      {/* 多站点选择:项目绑定了多个网址时,选一个进入标注模式 */}
      <Modal
        title="选择要标注的站点"
        open={siteModalOpen}
        onCancel={() => setSiteModalOpen(false)}
        footer={null}
        width={520}
      >
        <Paragraph type="secondary">
          本项目绑定了多个网站,请选择一个进入标注模式(将在新标签页打开并注入标注密钥):
        </Paragraph>
        <Space direction="vertical" style={{ width: "100%" }}>
          {parseBoundSites(project?.refer).map((site) => (
            <Button key={site} block style={{ textAlign: "left" }} onClick={() => openAnnotate(site)}>
              <i className="ri-global-line" style={{ marginRight: 8 }} />
              {site}
            </Button>
          ))}
        </Space>
      </Modal>
    </Card>
  );
};

export default EventAnnotations;
